import React, { FC, useState, useEffect, useCallback, useMemo } from "react";
import Badge from "shared/Badge/Badge";
import BookingCalandar from "../BookingCalandar";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DetailPageLayout from "../Layout_bbe";
import { useClient } from "../../../ClientContext";
import BookingWidget from "containers/Widgets/bookingWidget";
import { AmenitiesDetails } from "../(components)/AmenitiesDetails";
import ThingsToKnow from "../(components)/ThingsToKnow";
import MapComponent from "../(components)/map";
import axios from "axios";
import Reviews from "../(components)/Reviews";
import HtmlDescription from "../(components)/htmlDescription";
import { useBooking } from "context/BookingContext";
import { ListingBookingProvider } from "context/availabilitiesContext";
import { useListingBooking } from "context/availabilitiesContext";
//import { ListingDetailsType } from "data/types";
import { addDays, addMonths, lastDayOfMonth, format } from "date-fns";

import { CLIENT_LISTING_DETAILS_OBJECT, CLIENT_LISTING_DETAILS_RETURN } from "data/backEndTypes";

//translation
import { useTranslation } from "react-i18next";

// Skeleton Components
import ListingHeaderSkeleton from "./skeleton/ListingHeaderSkeleton";
import ListingTitleSkeleton from "./skeleton/ListingTitleSkeleton";
import StayInformationSkeleton from "./skeleton/StayinFoSkeleton";
import AmenitiesDetailsSkeleton from "./skeleton/AmenitiesDetailsSkeleton";
import BookingWidgetSkeleton from "./skeleton/BookingWidgetSkeleton";
import MapComponentSkeleton from "./skeleton/MapSkeleton";
import SectionDateRangeSkeleton from "./skeleton/SectionDateSkeleton";
import ThingsToKnowSkeleton from "./skeleton/ThingsToKnowSkeleton";
import ReviewsSkeleton from "./skeleton/ReviewsSkeleton";

// Utility function to get the correct number of beds based on internal_ID
function getBedsValue(internal_ID: string, beds: number): number {
  switch (internal_ID) {
    case "47c0250e-eb24-441f-8890-c7dfabdb7775":
      return 3;
    case "f9ae756d-be1b-4593-87aa-c245416e4ae7": // Kahala 
      return 6;
    default:
      return beds;
  }
}

// ------------------------------------------------
// Default values
// ------------------------------------------------
const today = new Date();
const tempCheckout = addDays(today, 5);

interface GuestyDetailPageContainerProps {
  listing: CLIENT_LISTING_DETAILS_OBJECT;
  availabilities: string[];
  loading: boolean;
}

export const getListingTitle = (listing: CLIENT_LISTING_DETAILS_OBJECT): string => {
  return listing?.wixCms?.item.title || listing?.title || "No title available";
};

const GuestyDetailPageContainer: FC<GuestyDetailPageContainerProps> =
  React.memo(({ listing, availabilities, loading }) => {
    const { setBookingState } = useBooking();
    const { setPropertyID, setAvailibitiesState } = useListingBooking();
    const navigate = useNavigate();
    const location = useLocation();
    const thisPathname = location.pathname;

    const { integration_TYPE, global_language, global_searh } = useClient();
    const { checkIn, checkOut, guestsCount } = global_searh.params || {};
    const { t } = useTranslation();

    const reviews = listing?.reviews;
    const calendar = (listing?.calendar as any)?.items;
    // ---------------------------------------------
    // Set booking state whenever we have listing ID
    // ---------------------------------------------
    useEffect(() => {
      if (listing?.id) {
        const dates: Array<Date> = listing.calendar?.items ?? [];
        setPropertyID(listing.id);
        setAvailibitiesState({
          isLoaded: true,
          availabilities: dates.map((date) =>
            typeof date === "string" ? date : format(date, "yyyy-MM-dd")
          ),
        });

        // Compute the correct price (lodgify vs. basePrice)
        let startingPrice: number | null = null;
        if (
          integration_TYPE === "lodgify" &&
          typeof listing?.prices?.lodgifyDisplayPrice === "number"
        ) {
          startingPrice = listing.prices.lodgifyDisplayPrice;
        } else if (typeof listing?.prices?.basePrice === "number") {
          startingPrice = listing.prices.basePrice;
        }

        setBookingState((prevState) => ({
          ...prevState,
          imgUrl: listing.galleryImgs?.[0]?.original ?? "",
          startingPrice,
          title: listing?.title ?? "",
          listingId: listing?.id ?? "",
          roomId: listing?.roomId ?? "",
          currency: listing?.base_currency ?? "",
          bath: listing?.bathrooms ?? 0,
          beds: listing?.bedrooms ?? 0,
          rating: 0,
          searchParams: {
            ...prevState.searchParams,
            checkIn: checkIn ?? today,
            checkOut: checkOut ?? tempCheckout,
            guests: guestsCount ?? 2,
          },
        }));
      }
    }, [
      listing,
      setPropertyID,
      setAvailibitiesState,
      setBookingState,
      integration_TYPE,
      global_searh,
    ]);

    // ---------------------------------------------------
    // Handlers & Renderers
    // ---------------------------------------------------
    // ↑ updated version – replaces the current entry instead of pushing
    const handleOpenModalImageGallery = useCallback(() => {
      navigate(`${thisPathname}?modal=PHOTO_TOUR_SCROLLABLE`, { replace: true });
    }, [thisPathname, navigate]);

    // METSA chalets must always show 3 beds (temp fix. to be moved to CMS)
    const { internal_ID } = useClient();
    // Destructure early for clarity
    const {
      galleryImgs = [],
      roomType,
      address,
      accommodates,
      beds,
      bedrooms,
      bathrooms,
      thingsToknow,
    } = listing || {};

    function getListingDescription(): JSX.Element {
      if (listing?.wixCms?.item.description) {
        return <>{listing.wixCms.item.description}</>;
      } else {
        return (
          <>
            <HtmlDescription description={listing.publicDescription?.summary || ""} />
          </>
        );
      }
    }
    // -------------
    // Render header
    // -------------
    const renderListingHeader = () => {
      if (loading) {
        return <ListingHeaderSkeleton />;
      }

      // If no images
      if (!galleryImgs.length) return null;

      return (
        <header className="rounded-md sm:rounded-xl">
          <div className="relative grid grid-cols-4 gap-1 sm:gap-2">
            {/* Main Image */}
            <div
              className="col-span-3 row-span-3 relative rounded-md sm:rounded-xl overflow-hidden cursor-pointer aspect-w-4 aspect-h-3"
              onClick={handleOpenModalImageGallery}
            >
              <img
                className="absolute inset-0 object-cover w-full h-full rounded-md sm:rounded-xl"
                src={galleryImgs[0]?.original}
                alt="photos 1"
              />
              <div className="absolute inset-0 bg-neutral-900 bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity"></div>
            </div>

            {/* Small Images on the Right */}
            <div className="col-span-1 row-span-3 flex flex-col gap-1 sm:gap-2">
              {galleryImgs.slice(1, 4).map((item, index) => (
                <div
                  key={index}
                  className="relative flex-1 rounded-md sm:rounded-xl overflow-hidden"
                >
                  <img
                    className="absolute inset-0 object-cover w-full h-full rounded-md sm:rounded-xl"
                    src={item?.original || ""}
                    alt=""
                  />
                  <div
                    className="absolute inset-0 bg-neutral-900 bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                    onClick={handleOpenModalImageGallery}
                  />
                </div>
              ))}
            </div>
          </div>
        </header>
      );
    };

    // -------------
    // Render title
    // -------------
    const renderTitle = () => {
      if (loading) {
        return <ListingTitleSkeleton />;
      }

      return (
        <div className="listingSection__wrap !space-y-6">
          {/* Room Type */}
          {!!roomType && (
            <div className="flex justify-between items-center">
              <Badge name={roomType} />
            </div>
          )}

          {/* Listing Title */}
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold">
            {getListingTitle(listing)}
          </h2>

          {/* Address */}
          <div className="flex items-center space-x-4">
            <span>
              <i className="las la-map-marker-alt"></i>
              <span className="ml-1">
                {address?.city}, {address?.state}
              </span>
            </span>
          </div>

          <div className="w-full border-b border-neutral-100 dark:border-neutral-700" />

          {/* Listing Basic Info */}
          <div className="flex items-center xl:justify-start space-x-6 xl:space-x-8 text-sm text-neutral-700 dark:text-neutral-300">
            {/* Guests */}
            {typeof accommodates === "number" && accommodates > 0 && (
              <div className="flex items-center space-x-3">
                <i className="las la-user text-2xl"></i>
                <span>
                  {accommodates} {t("guests")}
                </span>
              </div>
            )}
            {/* Beds */}
            {typeof beds === "number" && beds > 0 && (
              <div className="flex items-center space-x-3 hidden sm:flex">
                <i className="las la-bed text-2xl"></i>
                <span>
                  {getBedsValue(internal_ID, beds)} {t("beds")}
                </span>
              </div>
            )}
            {/* Bedrooms */}
            {typeof bedrooms === "number" && bedrooms > 0 && (
              <div className="flex items-center space-x-3">
                <i className="las la-door-open text-2xl"></i>
                <span>
                  {bedrooms} {t("bedrooms")}
                </span>
              </div>
            )}
            {/* Bathrooms */}
            {typeof bathrooms === "number" && bathrooms > 0 && (
              <div className="flex items-center space-x-3">
                <i className="las la-bath text-2xl"></i>
                <span>
                  {bathrooms} {t("baths")}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    };
    // -------------
    // Render "Stay Information"
    // -------------
    const renderStayInformation = () => {
      if (loading) {
        return <StayInformationSkeleton />;
      }

      return (
        <div className="listingSection__wrap">
          <h2 className="text-2xl font-semibold">{t("Stay information")}</h2>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
          <div className="text-neutral-6000 dark:text-neutral-300">
            {getListingDescription()}
          </div>
        </div>
      );
    };
    // -------------
    // Render the container
    // -------------
   console.log("RENDERING DETAIL PAGE WITH LISTING:", listing)
    return (
      <div className="nc-ListingStayDetailPage">
        {renderListingHeader()}

        <main className="relative z-10 mt-11 flex flex-col lg:flex-row">
          {/* LEFT MAIN */}
          <div className="w-full lg:w-3/5 xl:w-2/3 space-y-8 lg:space-y-10 lg:pr-10">
            {renderTitle()}
            {renderStayInformation()}

            {/* Amenities */}
            {loading ? (
              <AmenitiesDetailsSkeleton />
            ) : (
              <AmenitiesDetails amenities={listing?.amenities || []} />
            )}

            {/* Reviews */}
            {loading ? (
              <ReviewsSkeleton />
            ) : !!reviews && Object.keys(reviews).length > 0 && reviews.total > 0 && internal_ID !== "66945d50-54fc-45ea-9f53-6e2395f28c21" ? (
              <Reviews reviews={reviews} />
            ) : null}

            {/* Availability / DateRange */}
            {loading ? (
              <SectionDateRangeSkeleton />
            ) : (
              <BookingCalandar
                id={listing.id}
                isLoading={loading}
                dates={calendar || []}
              />
            )}

            {/* Map */}
            {loading ? (
              <MapComponentSkeleton />
            ) : (
              <MapComponent address={address || {}} />
            )}

            {/* Things to know */}
            {loading ? (
              <ThingsToKnowSkeleton />
            ) : (
              <ThingsToKnow thingsToknow={thingsToknow || {}} />
            )}
          </div>

          {/* RIGHT BOOKING WIDGET */}
          <div className="hidden lg:block flex-grow mt-14 lg:mt-0">
            <div className="sticky top-28">
              {loading ? (
                <BookingWidgetSkeleton />
              ) : (
                <BookingWidget
                  listingPrices={listing?.prices}
                  listingId={listing.id}
                  dates={calendar}
                />
              )}
            </div>
          </div>
        </main>
      </div>
    );
  });

export default function ListingDetailPage() {
  const { id } = useParams();
  const [listing, setListing] = useState<any>();
  const [availabilities, setAvailabilities] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { internal_ID, actions, global_language, lodgifySite, wix_cms_api } =
    useClient();
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const dateRangeStartString = format(new Date(), "yyyy-MM-dd");
  const dateRangeEndString = format(
    lastDayOfMonth(addMonths(new Date(), 12)),
    "yyyy-MM-dd"
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const response = await axios.post(`${SERVER_URL}/api/controller`, {
        action: actions.fetchListingDetails,
        internal_ID,
        params: {
          default_Lang: global_language,
          websiteId: lodgifySite.id,
          //  listingId:"362754",
          listingId: id,
          availabilities: {
            fromDate: dateRangeStartString,
            toDate: dateRangeEndString,
          },
        },
        wix_params: {
          wix_req: wix_cms_api.cmsRequest,
          siteURL: wix_cms_api.cmsURL || "",
          listingId: parseInt(id || "0"),
        },
      });

      const res_listing = response.data.item;
      const res_calendar = response.data.item.calendar.items;

      setListing(res_listing);
      setAvailabilities(res_calendar);
    } catch (err) {
      setError("Error fetching listing data: " + (err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [
    id,
    internal_ID,
    actions,
    global_language,
    lodgifySite,
    SERVER_URL,
    dateRangeStartString,
    dateRangeEndString,
    wix_cms_api,
  ]);

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id, fetchData]);

  // ----------------------------------------------------------------
  // Render logic
  // ----------------------------------------------------------------
  if (!id) return <>Missing listing ID</>;
  if (error) return <div className="error">{error}</div>;

  const listingImgs =
    listing?.galleryImgs?.map((img: any) => img.original) || [];

  return (
    <ListingBookingProvider>
      <DetailPageLayout listingImgs={listingImgs}>
        <div className="mb-20">
          <GuestyDetailPageContainer
            loading={loading}
            listing={listing ?? ({} as any)}
            availabilities={availabilities ?? []}
          />
        </div>
      </DetailPageLayout>
    </ListingBookingProvider>
  );
}
